import React, { useEffect, useState } from 'react';
import { Phone, Menu, X } from 'lucide-react';
import { contactInfo } from '../mock';
import { useBooking } from '../context/BookingContext';
import { useT } from '../i18n/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';
import DarkModeToggle from './DarkModeToggle';

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { openModal } = useBooking();
  const { t } = useT();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const links = [
    { label: t('nav.home'), href: '#home' },
    { label: t('nav.services'), href: '#services' },
    { label: t('nav.areas'), href: '#areas' },
    { label: t('nav.faq'), href: '#faq' },
    { label: t('nav.contact'), href: '#contact' },
  ];

  return (
    <>
      {/* Top bar */}
      <div className="hidden md:flex items-center justify-between px-10 py-2 bg-[#1a2851] text-white text-sm">
        <div className="flex items-center gap-6">
          <span className="opacity-90">{t('top.hours')}</span>
          <span className="opacity-40">|</span>
          <span className="opacity-90">{t('top.free')}</span>
        </div>
        <div className="flex items-center gap-4">
          <LanguageSwitcher />
          <DarkModeToggle inTopbar />
          <a
            href={`tel:${contactInfo.phone.replace(/\s/g, '')}`}
            className="flex items-center gap-2 hover:text-[#4dbfae] transition-colors"
          >
            <Phone size={14} />
            <span>{t('top.callUs')} {contactInfo.phone}</span>
          </a>
        </div>
      </div>

      {/* Main nav */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white/95 backdrop-blur shadow-md' : 'bg-white'
        }`}
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 lg:px-10 py-4">
          <a href="#home" className="flex items-center gap-3">
            <img
              src="/logo/gicleanly-logo.jpg"
              alt="GiCleanly"
              className="w-11 h-11 rounded-full object-cover shadow-sm"
            />
            <div className="leading-tight">
              <p className="font-serif text-xl font-bold text-[#1a2851]">GiCleanly</p>
              <p className="text-[10px] tracking-[.22em] uppercase text-[#4dbfae] -mt-1">Clean Spaces, Deep Clear</p>
            </div>
          </a>

          <nav className="hidden lg:flex items-center gap-7">
            {links.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className="uc-nav-link text-sm font-medium text-[#2b2e33]"
              >
                {link.label}
              </a>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <button onClick={openModal} className="uc-btn text-sm">
              {t('nav.freeEstimate')}
            </button>
          </div>

          <button
            className="lg:hidden p-2 text-[#1a2851]"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden bg-white border-t px-6 py-4 uc-fade-up">
            <div className="flex flex-col gap-3">
              {links.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={() => setOpen(false)}
                  className="py-2 text-[#2b2e33] hover:text-[#4dbfae] transition-colors"
                >
                  {link.label}
                </a>
              ))}
              <div className="pt-2 flex items-center gap-3">
                <LanguageSwitcher compact />
                <DarkModeToggle />
              </div>
              <button
                onClick={() => { setOpen(false); openModal(); }}
                className="uc-btn w-fit mt-2"
              >
                {t('nav.freeEstimate')}
              </button>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
